import {
  PosDataLevel,
  PosGetItem,
  PosItem,
  RegisterPos,
} from '@/domain/models/Pos';
import { DASHBOARD_API_ORIGIN } from '@/global/env';
import { RestClientInterface } from '@/lib/restClient';

// import { posGetItemAtBrandDeliveryService } from './mocks';

export interface getParams {
  providerId?: number;
  dataLevel?: PosDataLevel;
}

export interface uploadParams {
  providerId: number;
  uploadData: RegisterPos[];
}

interface Interface {
  fetch: (params: getParams) => Promise<PosGetItem>;
  upload: (params: uploadParams) => Promise<void>;
}

// NOTE 入力値の管理で使用
type HandlePos = {
  posCode: string;
  promotionOneFreePosCode: string;
};

export type PosItemAndHandlePos = PosItem & HandlePos;

export const posItemColumn = {
  itemName: { key: 'item_name', label: '商品名' },
  brandName: { key: 'brand_name', label: 'ブランド名' },
  deliveryServiceName: {
    key: 'delivery_service_name',
    label: 'デリバリーサービス',
  },
  authStoreName: { key: 'auth_store_name', label: '拠点名' },
  posItemCode: { key: 'pos_item_code', label: 'POSコード' },
  promotionOneFreePosCode: {
    key: 'buy_one_get_one_free_pos_item_code',
    label: 'もう一品無料商品のPOSコード',
  },
  metaData: { key: 'meta_data', label: 'meta_data' },
} as const;

const {
  posItemCode,
  promotionOneFreePosCode,
  metaData,
  itemName,
  deliveryServiceName,
  brandName,
  authStoreName,
} = posItemColumn;

export const posPromotionOneFreeCSVHeaders = Object.values(posItemColumn);
export type PosPromotionOneFreeCSVHeaders =
  typeof posPromotionOneFreeCSVHeaders[number]['key'];

// CSVで使う共通列
const csvDataColoumns = [posItemCode, promotionOneFreePosCode, metaData];

// PosDataLevelに対応をした各出力をする。ポリモーフィズム
export const mappingPosDataLevel = {
  brand_delivery_service: {
    ths: [deliveryServiceName.label, brandName.label],
    tds: [deliveryServiceName.key, brandName.key],
    csvData: [itemName, deliveryServiceName, brandName, ...csvDataColoumns],
    linkedStores: [
      {
        key: authStoreName.key,
        label: authStoreName.label,
      },
    ],
    toJa: 'ブランド × デリバリーサービス',
  },
  delivery_service: {
    ths: [deliveryServiceName.label],
    tds: [deliveryServiceName.key],
    csvData: [itemName, deliveryServiceName, ...csvDataColoumns],
    linkedStores: [
      {
        key: authStoreName.key,
        label: authStoreName.label,
      },
      {
        key: brandName.key,
        label: brandName.label,
      },
    ],
    toJa: 'デリバリーサービス',
  },
  brand: {
    ths: [brandName.label],
    tds: [brandName.key],
    csvData: [itemName, brandName, ...csvDataColoumns],
    linkedStores: [
      {
        key: authStoreName.key,
        label: authStoreName.label,
      },
      {
        key: deliveryServiceName.key,
        label: deliveryServiceName.label,
      },
    ],
    toJa: 'ブランド',
  },
  auth_store: {
    ths: [authStoreName.label],
    tds: [authStoreName.key],
    csvData: [itemName, authStoreName, ...csvDataColoumns],
    linkedStores: [
      {
        key: brandName.key,
        label: brandName.label,
      },
      {
        key: deliveryServiceName.key,
        label: deliveryServiceName.label,
      },
    ],
    toJa: '拠点（店舗）',
  },
};

type MappingPosDataLevel = typeof mappingPosDataLevel;

type MappingPosDataLevelColumn<K extends keyof MappingPosDataLevel> =
  MappingPosDataLevel[K];

type MappingPosDataLevelColumns = MappingPosDataLevelColumn<
  'delivery_service' | 'brand' | 'brand_delivery_service' | 'auth_store'
>;

export type MappingPosDataLevelThs = MappingPosDataLevelColumns['ths'][number];
export type MappingPosDataLevelTds = MappingPosDataLevelColumns['tds'][number];
export type MappingPosDataLevelCsvData =
  MappingPosDataLevelColumns['csvData'][number];
export type MappingPosDataLevelLinkedStores =
  MappingPosDataLevelColumns['linkedStores'][number];

export class PosService implements Interface {
  constructor(private readonly restClient: RestClientInterface) {}

  public async fetch(params: getParams): Promise<PosGetItem> {
    if (!params.providerId) return {};
    const queryParams = this.buildQueryParams(params);
    /**
     * sanmple：/api/pos-providers/1111/items?pos_data_level=auth_store
     **/
    const res = await this.restClient.get<PosGetItem>(
      `${DASHBOARD_API_ORIGIN}/api/pos-providers/${params.providerId.toString()}/items?${queryParams.toString()}` as const
    );
    /* NOTE APIのモックを使うとPosDataLevelがauth_storeを想定したものになるので、テーブルの列がずれるので `posGetItem, posGetItem02`を使用し検証する。*/
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // return posGetItemAtBrandDeliveryService as any;
    return res;
  }

  public async upload(params: uploadParams): Promise<void> {
    const res = await this.restClient.put<RegisterPos[], void>(
      `${DASHBOARD_API_ORIGIN}/api/pos-providers/${params.providerId.toString()}/items` as const,

      params.uploadData
    );
    return res;
  }

  private buildQueryParams(params: getParams): URLSearchParams {
    const urlParams = new URLSearchParams();

    if (params.dataLevel) {
      urlParams.append('pos_data_level', params.dataLevel);
    }

    return urlParams;
  }
}
